import {createAsyncThunk} from "@reduxjs/toolkit";
import {
    getActiveBlock, getAfterMessageBlock, getNextBlock, getNextId, hasAfterMessage, shouldFinishChat, shouldGoNext,
    shouldUpdateActiveGroup
} from "@/helpers/Flow";
import {
    addBlock as addBlockReducer, clearTimerID, finishChat, noData, resetFlow as resetFlowReducer,
    rewindToBlock as rewindToBlockReducer, setActiveGroupId, setData, setDataLoading,
    setHasConversationSubmitFailed, setIsSubmittingConversation, setTimerID, showLoading
} from "@/store/slices/chatbotFlow";

import {resetSelectedOptions} from "@/store/slices/chatbotInput";
import {apiClient} from "@/helpers/axios";
import store from "@/store/store";
import {setSecondsPassed, setStartTime} from "@/store/slices/chatbotVisibility";
import BlockTypes from "@/constants/BlockTypes";

export const processFlow = createAsyncThunk(
    'chatbotFlow/processFlow',
    async (input, thunkAPI) => {
        const {
            flow, activeGroupId, conversation, isFinished, loading, asyncMessageTimerID
        } = thunkAPI.getState().chatbotFlow;
        if (conversation.length === 0) { //load the first block if it's not loaded
            thunkAPI.dispatch(addBlock(flow.groups[0].blocks[0]))
        }

        if (isFinished) //don't process flow if the chat has finished
            return


        if (loading || asyncMessageTimerID !== 0) //don't process flow if the chat is in loading state
            return

        let activeBlock = getActiveBlock(conversation) //get Current active block from conversations

        if (!shouldGoNext(activeBlock)) //check if user has entered his/her answer
            return

        if (shouldFinishChat(activeBlock) && !hasAfterMessage(activeBlock)) { //check if next actions are END_CHAT
            thunkAPI.dispatch(finishChat())
            thunkAPI.dispatch(resetSelectedOptions())
            thunkAPI.dispatch(submitConversation())
            return
        }

        let nextBlock = getNextBlock(activeBlock, activeGroupId, flow)

        if (hasAfterMessage(activeBlock)) {
            nextBlock = thunkAPI.dispatch(addBlock(getAfterMessageBlock(activeBlock)));
        }

        if (shouldUpdateActiveGroup(activeBlock)) {
            const newGroupId = getNextId(activeBlock);
            thunkAPI.dispatch(setActiveGroupId(newGroupId)) //it's important to update activeGroupId after finding the next block, not before it.
        }

        if (typeof nextBlock !== 'undefined') { //Check if it's the last block in the group
            if (nextBlock?.blockType === BlockTypes.DATA)
                thunkAPI.dispatch(addBlockReducer(nextBlock))
            else
                thunkAPI.dispatch(addBlock(nextBlock));
        } else {
            thunkAPI.dispatch(finishChat())
            thunkAPI.dispatch(submitConversation())
            thunkAPI.dispatch(resetSelectedOptions())
        }
    }
)
  
  
export const submitConversation = createAsyncThunk(
    'chatbotFlow/submitConversation',
    async (_, thunkAPI) => {
        const { chatbotDetails, chatbotFlow, chatbotVisibility } = thunkAPI.getState();
        const { UUID, candidateID, sourceID, exid } = chatbotDetails;
        const { conversation, isFinished, isSubmittingConversation } = chatbotFlow;

        if (isSubmittingConversation) return;

        // Calculate time interval
        const { startTime, secondsPassed } = chatbotVisibility;
        const seconds = ((new Date()).getTime() - (new Date(startTime)).getTime()) / 1000;
        const duration = Math.min(Math.round(secondsPassed + seconds), 32767); 

        thunkAPI.dispatch(setIsSubmittingConversation(true));
        thunkAPI.dispatch(setHasConversationSubmitFailed(false));

        const payload = {
        raw_data: conversation,
        is_completed: isFinished,
        duration,
        };

        const queryParams = { id: candidateID, source: sourceID, exid };

        // Submit the conversation to the server via sw
        await offloadToServiceWorker(payload, queryParams, UUID, thunkAPI.dispatch);
        thunkAPI.dispatch(setIsSubmittingConversation(false));       
    }
);

const buildQueryString = (params) => {
    if (!params) return '';
    const filteredParams = Object.fromEntries(
        Object.entries(params).filter(([, value]) => value !== null && value !== undefined)
    );
    return Object.keys(filteredParams).length
        ? `?${new URLSearchParams(filteredParams).toString()}`
        : '';
};

const submitConversationDirectly = async (payload, queryParams, UUID, dispatch) => {
    const queryString = buildQueryString(queryParams);
    const endpoint = `/assistants/${UUID}/conversations/${queryString}`;
    try {
        await apiClient.post(endpoint, payload);
        console.info(`Conversation submitted successfully for chatbot id:${UUID}.`);
    } catch (error) {
        console.error(`Failed to submit conversation for chatbot id:${UUID}.`, error);
        dispatch(setHasConversationSubmitFailed(true));
    }
};

const offloadToServiceWorker = (payload, queryParams, UUID, dispatch) => {
    if ('serviceWorker' in navigator && navigator.serviceWorker?.controller) {
        navigator.serviceWorker.controller.postMessage({
            type: 'SUBMIT_CONVERSATION',
            data: {
                ...payload,
                params: queryParams,
                UUID,
            }
        });
        // The service worker reports the outcome back through a client message.
        return Promise.resolve();
    }
    return submitConversationDirectly(payload, queryParams, UUID, dispatch);
};

if ('serviceWorker' in navigator && navigator.serviceWorker) {
    navigator.serviceWorker.addEventListener('message', (event) => {
        const { type, status, message } = event.data;
        if (type === 'CONVERSATION_SUBMIT_STATUS') {
            status === 'error' ? console.error(message) : console.info(message)
            store.dispatch(setHasConversationSubmitFailed(status === 'error'));
        }
    });
}


export const searchCandidates = createAsyncThunk(
    'chatbotFlow/searchCandidates',
    async (action, thunkAPI) => {
        thunkAPI.dispatch(setDataLoading(true))
        const {chatbotDetails, chatbotFlow, chatbotVisibility} = thunkAPI.getState();
        const {UUID} = chatbotDetails;
        const {conversation, isFinished} = chatbotFlow;

        //calculate time interval
        const {startTime, secondsPassed} = chatbotVisibility;
        const seconds = ((new Date()).getTime() - (new Date(startTime)).getTime()) / 1000;
        return apiClient.put(`/assistants/${UUID}/candidate-search/`, {
            raw_data: conversation,
            is_completed: isFinished,
            duration: Math.min(Math.round(secondsPassed + seconds), 32767)
        })
            .then(function (response) {
                // handle success
                if (response.status === 200) {
                    thunkAPI.dispatch(setData(response?.data))
                    if (response?.data?.length === 0)
                        thunkAPI.dispatch(noData())
                } else {
                    thunkAPI.dispatch(setData([]))
                    thunkAPI.dispatch(noData())
                }
                thunkAPI.dispatch(setDataLoading(false))
            })
            .catch(function (error) {
                thunkAPI.dispatch(setDataLoading(false))
                thunkAPI.dispatch(setData([]))
                thunkAPI.dispatch(noData())
                console.error("elay.io chatbot couldn't connect to the server")
                console.error(error)
            })
    }
)

export const searchJobs = createAsyncThunk(
    'chatbotFlow/searchJobs',
    async (action, thunkAPI) => {
        thunkAPI.dispatch(setDataLoading(true))
        const {chatbotDetails, chatbotFlow, chatbotVisibility} = thunkAPI.getState();
        const {UUID} = chatbotDetails;
        const {conversation, isFinished} = chatbotFlow;

        //calculate time interval
        const {startTime, secondsPassed} = chatbotVisibility;
        const seconds = ((new Date()).getTime() - (new Date(startTime)).getTime()) / 1000;
        return apiClient.put(`/assistants/${UUID}/job-search/`, {
            raw_data: conversation,
            is_completed: isFinished,
            duration: Math.min(Math.round(secondsPassed + seconds), 32767)
        })
            .then(function (response) {
                if (response.status === 200) {
                    thunkAPI.dispatch(setData(response?.data))
                    if (response?.data?.length === 0)
                        thunkAPI.dispatch(noData())
                } else {
                    thunkAPI.dispatch(setData([]))
                    thunkAPI.dispatch(noData())
                }
                thunkAPI.dispatch(setDataLoading(false));
            })
            .catch(function (error) {
                thunkAPI.dispatch(setDataLoading(false))
                thunkAPI.dispatch(setData([]))
                thunkAPI.dispatch(noData())
                console.error("elay.io chatbot couldn't connect to the server")
                console.error(error)
            })
    }
)


export const resetFlow = createAsyncThunk(
    'chatbotFlow/resetFlowThunk',
    async (block, thunkAPI) => {
        const state = thunkAPI.getState().chatbotFlow
        clearTimeout(state.asyncMessageTimerID);
        thunkAPI.dispatch(setStartTime(new Date().toISOString()))
        thunkAPI.dispatch(setSecondsPassed(0))
        thunkAPI.dispatch(clearTimerID())
        thunkAPI.dispatch(resetFlowReducer())
        thunkAPI.dispatch(resetSelectedOptions())
    }
)

export const rewindToBlock = createAsyncThunk(
    'chatbotFlow/rewindToBlockThunk',
    async (blockIndex, thunkAPI) => {
        const state = thunkAPI.getState().chatbotFlow
        clearTimeout(state.asyncMessageTimerID);
        thunkAPI.dispatch(clearTimerID())
        thunkAPI.dispatch(rewindToBlockReducer(blockIndex))
    }
)

export const addBlock = createAsyncThunk( //Add a block with loading
    'chatbotFlow/addBlockDelayed',
    async (block, thunkAPI) => {
        const state = thunkAPI.getState().chatbotFlow;
        const numberOfHalfLines = Math.round(block?.message?.length / 15) || 1
        const rawDelay = numberOfHalfLines < 2 ? 800 : numberOfHalfLines * 499
        const delay = rawDelay > 3500 ? 3500 : rawDelay
        if (state.loading === false) {
            clearTimeout(state.asyncMessageTimerID);
            if (block?.options?.default_answer === true) { //if it was default answer, don't show loading and add it to the conversation immediately.
                thunkAPI.dispatch(addBlockReducer(block))
            } else {
                thunkAPI.dispatch(showLoading(true))
                const timerID = setTimeout(() => {
                    thunkAPI.dispatch(addBlockReducer(block))
                    thunkAPI.dispatch(clearTimerID())
                    thunkAPI.dispatch(showLoading(false))
                }, delay)
                thunkAPI.dispatch(setTimerID(timerID))
            }
        }
    }
)
